export interface NgsildPreviewTable {
  headers: string[];
  rows: unknown[][];
}

const DIRECT_PREVIEW_EXTENSIONS = new Set(['.csv', '.txt']);

export function isDirectlyPreviewableFileName(fileName: string): boolean {
  const dot = fileName.lastIndexOf('.');
  const ext = dot >= 0 ? `.${fileName.slice(dot + 1).toLowerCase()}` : '';
  return DIRECT_PREVIEW_EXTENSIONS.has(ext);
}

/** True when the preview API could not render the file server-side. */
export function isPreviewUnsupportedResponse(previewData: unknown): boolean {
  const content = extractPreviewContentNode(previewData);
  if (!content || typeof content !== 'object') {
    return false;
  }
  const node = content as Record<string, unknown>;
  return node['previewSupported'] === false;
}

/** Human-readable reason from an unsupported preview API payload. */
export function getPreviewUnsupportedMessage(previewData: unknown): string {
  const content = extractPreviewContentNode(previewData);
  if (!content || typeof content !== 'object') {
    return '';
  }
  const message = (content as Record<string, unknown>)['message'];
  return typeof message === 'string' ? message.trim() : '';
}

function extractPreviewContentNode(previewData: unknown): unknown {
  if (previewData == null || typeof previewData !== 'object') {
    return null;
  }
  const body = previewData as Record<string, unknown>;
  const data = body['data'];
  if (data && typeof data === 'object') {
    const dataNode = data as Record<string, unknown>;
    if (dataNode['content'] != null) {
      return dataNode['content'];
    }
  }
  if (body['content'] != null) {
    return body['content'];
  }
  return null;
}

/** Parse Files Connect preview payload into a table (CSV / JSON rows). */
export function parseNgsildPreviewData(
  previewData: unknown,
): NgsildPreviewTable | null {
  try {
    if (previewData == null) {
      return null;
    }
    if (typeof previewData === 'string') {
      return parseNgsildCsvPreview(previewData);
    }

    const body = previewData as Record<string, unknown>;

    if (body['success'] && (body['data'] as Record<string, unknown>)?.['content']) {
      return rowsFromObjects(
        (body['data'] as { content: { data?: unknown[] } }).content.data,
      );
    }
    if ((body['data'] as Record<string, unknown>)?.['content']) {
      return rowsFromObjects(
        (body['data'] as { content: { data?: unknown[] } }).content.data,
      );
    }
    if ((body['content'] as { data?: unknown[] })?.data) {
      return rowsFromObjects((body['content'] as { data?: unknown[] }).data);
    }
    if (Array.isArray(body['data']) && body['data'].length > 0) {
      const data = body['data'] as unknown[];
      if (typeof data[0] === 'object' && data[0] != null && !Array.isArray(data[0])) {
        return rowsFromObjects(data as Record<string, unknown>[]);
      }
      const headers = data[0] as string[];
      return { headers, rows: data.slice(1) as unknown[][] };
    }
    if (Array.isArray(previewData) && previewData.length > 0) {
      return rowsFromObjects(previewData as Record<string, unknown>[]);
    }

    return null;
  } catch (error) {
    console.error('Error parsing preview data:', error);
    return null;
  }
}

function rowsFromObjects(data: unknown[] | undefined): NgsildPreviewTable | null {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }
  const first = data[0];
  if (typeof first !== 'object' || first == null || Array.isArray(first)) {
    return null;
  }
  const headers = Object.keys(first as Record<string, unknown>);
  const rows = data.map((row) =>
    Object.values(row as Record<string, unknown>),
  );
  return { headers, rows };
}

function parseNgsildCsvPreview(csvString: string): NgsildPreviewTable {
  const lines = csvString.trim().split('\n');
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((line) => parseLine(line));
  return { headers, rows };
}
